"use client";

/**
 * Login hook — orchestrates the full login flow including MFA and session conflicts.
 *
 * Session conflicts can now surface at three points:
 *   1. password submit  → /auth/login          (handled via loginConfirm)
 *   2. MFA submit       → /auth/login/mfa      (replay with force=true)
 *   3. WebAuthn complete → /auth/webauthn/authenticate/complete (replay with force=true)
 *
 * We remember the *pending takeover action* — what to call after the user
 * clicks "Sign in here, end other session" — so the modal handler does not
 * need to know which auth method produced the conflict.
 *
 * Session conflict persistence:
 *   When a user confirms takeover, we set a sessionStorage flag. If they
 *   refresh the page mid-flow and re-submit, we auto-confirm without
 *   showing the dialog again. The flag is cleared on success, explicit
 *   reset, or tab close.
 */

import { needsTotpSetup } from "@/lib/auth/needs-totp-setup";
import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth/context";
import {
  login,
  loginConfirm,
  loginMFA,
  completeWebAuthnAuthentication,
  type LoginResult,
} from "@/lib/api/auth";
import { getDeviceFingerprint, getDeviceName } from "@/lib/auth/device";

const SESSION_CONFLICT_KEY = "mi_session_conflict_pending";

type LoginState =
  | { step: "idle" }
  | { step: "loading" }
  | { step: "redirecting" }
  | { step: "mfa_required"; userId: string; mfaMethods: string[] }
  | { step: "session_conflict"; activeDevice: string; lastActivity: string }
  | {
      step: "error";
      code: string;
      message: string;
      retryAfter?: number;
      // Surface the lockout deadline so the login page can render a
      // precise countdown for ``account_locked`` (423) responses.
      lockedUntil?: string;
    };

// What to invoke when the user clicks "Sign in here, end other session".
// Each variant captures the data needed to replay the originating call with
// force=true. We never store passwords or credentials beyond the lifetime of
// the in-flight login attempt.
type PendingTakeover =
  | {
      kind: "password";
      email: string;
      password: string;
    }
  | {
      kind: "mfa";
      userId: string;
      mfaCode: string;
    }
  | {
      kind: "webauthn";
      userId: string;
      credential: Record<string, unknown>;
    };

interface UseLoginReturn {
  state: LoginState;
  submitLogin: (email: string, password: string) => Promise<void>;
  submitMFA: (code: string) => Promise<void>;
  submitWebAuthnMFA: (assertionCredential: Record<string, unknown>) => Promise<void>;
  confirmSessionTakeover: () => Promise<void>;
  reset: () => void;
}

function clearConflictFlag() {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(SESSION_CONFLICT_KEY);
  }
}

function setConflictFlag() {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(SESSION_CONFLICT_KEY, "1");
  }
}

function isConflictAcknowledged(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(SESSION_CONFLICT_KEY) === "1";
}

export function useLogin(): UseLoginReturn {
  const [state, setState] = useState<LoginState>({ step: "idle" });
  const [pendingTakeover, setPendingTakeover] = useState<PendingTakeover | null>(null);
  const { setUserFromLogin } = useAuth();
  const router = useRouter();

  const handleResult = useCallback(
    (result: LoginResult) => {
      switch (result.type) {
        case "success":
          clearConflictFlag();
          setPendingTakeover(null);
          setState({ step: "redirecting" });
          setUserFromLogin(result.data);
          if (result.data.must_change_password) {
            router.replace("/password/change");
          } else if (needsTotpSetup(result.data)) {
            router.replace("/mfa/setup");
          } else if (result.data.status === "pending") {
            router.replace("/onboarding");
          } else if (result.data.is_global_admin && !result.data.company_id) {
            router.replace("/admin/companies");
          } else {
            router.replace("/my-dashboard");
          }
          break;
        case "mfa_required":
          setState({
            step: "mfa_required",
            userId: result.user_id,
            mfaMethods: result.mfa_methods,
          });
          break;
        case "session_conflict":
          setState({
            step: "session_conflict",
            activeDevice: result.active_device,
            lastActivity: result.last_activity,
          });
          break;
        case "error":
          setState({
            step: "error",
            code: result.code,
            message: result.message,
            ...(result.retryAfter !== undefined ? { retryAfter: result.retryAfter } : {}),
            ...(result.lockedUntil !== undefined ? { lockedUntil: result.lockedUntil } : {}),
          });
          break;
      }
    },
    [setUserFromLogin, router],
  );

  const submitLogin = useCallback(
    async (email: string, password: string) => {
      setState({ step: "loading" });
      setPendingTakeover({ kind: "password", email, password });

      try {
        const fingerprint = await getDeviceFingerprint();
        const deviceName = getDeviceName();

        const result = await login({
          email,
          password,
          device_fingerprint: fingerprint,
          device_name: deviceName,
        });

        // If we get a session conflict but the user already acknowledged it in
        // this tab session (e.g. they refreshed mid-flow), auto-confirm.
        if (result.type === "session_conflict" && isConflictAcknowledged()) {
          setState({ step: "loading" });
          try {
            const confirmResult = await loginConfirm({
              email,
              password,
              device_fingerprint: fingerprint,
              device_name: deviceName,
            });
            handleResult(confirmResult);
          } catch {
            setState({
              step: "error",
              code: "network_error",
              message: "Unable to connect to the server",
            });
          }
          return;
        }

        handleResult(result);
      } catch {
        setState({
          step: "error",
          code: "network_error",
          message: "Unable to connect to the server",
        });
      }
    },
    [handleResult],
  );

  const submitMFA = useCallback(
    async (code: string) => {
      if (state.step !== "mfa_required") return;

      // Stay in mfa_required so MFAForm remains mounted with its own
      // isLoading state. handleResult transitions to "redirecting" (Spinner)
      // or "error" on completion.
      setPendingTakeover({ kind: "mfa", userId: state.userId, mfaCode: code });

      try {
        const fingerprint = await getDeviceFingerprint();
        const deviceName = getDeviceName();

        // If the user already confirmed the takeover at the password step,
        // propagate that acknowledgement to the MFA call. Otherwise the
        // backend runs the conflict check again on /auth/login/mfa — the
        // old session only gets killed inside create_session, which fires
        // AFTER the MFA conflict check — and the user gets the conflict
        // modal a second time.
        const force = isConflictAcknowledged();

        const result = await loginMFA({
          user_id: state.userId,
          mfa_code: code,
          device_fingerprint: fingerprint,
          device_name: deviceName,
          force,
        });

        handleResult(result);
      } catch {
        setState({
          step: "error",
          code: "network_error",
          message: "Unable to connect to the server",
        });
      }
    },
    [state, handleResult],
  );

  const confirmSessionTakeover = useCallback(async () => {
    if (!pendingTakeover) return;

    const pending = pendingTakeover;
    setPendingTakeover(null);

    // Persist acknowledgement so if the user refreshes before redirect completes,
    // we auto-confirm without showing the dialog again.
    setConflictFlag();
    setState({ step: "loading" });

    try {
      const fingerprint = await getDeviceFingerprint();
      const deviceName = getDeviceName();

      let result: LoginResult;
      if (pending.kind === "password") {
        result = await loginConfirm({
          email: pending.email,
          password: pending.password,
          device_fingerprint: fingerprint,
          device_name: deviceName,
        });
      } else if (pending.kind === "mfa") {
        result = await loginMFA({
          user_id: pending.userId,
          mfa_code: pending.mfaCode,
          device_fingerprint: fingerprint,
          device_name: deviceName,
          force: true,
        });
      } else {
        result = await completeWebAuthnAuthentication(
          pending.userId,
          pending.credential,
          fingerprint,
          deviceName,
          true,
        );
      }

      handleResult(result);
    } catch {
      setState({
        step: "error",
        code: "network_error",
        message: "Unable to connect to the server",
      });
    }
  }, [pendingTakeover, handleResult]);

  const submitWebAuthnMFA = useCallback(
    async (assertionCredential: Record<string, unknown>) => {
      if (state.step !== "mfa_required") return;

      // Stay in mfa_required so MFAForm remains mounted with its own
      // isLoading state. handleResult transitions to "redirecting" (Spinner)
      // or "error" on completion.
      setPendingTakeover({
        kind: "webauthn",
        userId: state.userId,
        credential: assertionCredential,
      });

      try {
        const fingerprint = await getDeviceFingerprint();
        const deviceName = getDeviceName();

        // Carry the password-step takeover acknowledgement through to the
        // WebAuthn call, same reason as the TOTP path above.
        const force = isConflictAcknowledged();

        const result = await completeWebAuthnAuthentication(
          state.userId,
          assertionCredential,
          fingerprint,
          deviceName,
          force,
        );

        handleResult(result);
      } catch {
        setState({
          step: "error",
          code: "network_error",
          message: "Unable to connect to the server",
        });
      }
    },
    [state, handleResult],
  );

  const reset = useCallback(() => {
    clearConflictFlag();
    setPendingTakeover(null);
    setState({ step: "idle" });
  }, []);

  return {
    state,
    submitLogin,
    submitMFA,
    submitWebAuthnMFA,
    confirmSessionTakeover,
    reset,
  };
}
